import { DelayedSkeleton } from '@/components/ui/delayed-skeleton';
import {
	DetailField,
	DetailFieldsGrid,
	DetailSection
} from '@/components/ui/details-panel';
import { EntityLink } from '@/components/ui/entity-link';
import {
	EntityDetailsHeader,
	type EntityDetailsHeaderActions
} from '@/components/ui/entity-details-header';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabLink } from '@/components/ui/tabs';
import { AuditTab } from '@/components/ui/audit-tab';
import type { TariffQuery } from '@/graphql/generated';
import { useEntityModalContext } from '@/contexts/EntityModalContext';
import { useOne } from '@/hooks/resourceQueries';
import { useDelayedLoading } from '@/hooks/useDelayedLoading';
import { useRateTypeLookup } from '@/hooks/useRateTypeLookup';
import { FileText, History, BookOpen, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TARIFF_QUERY } from './tariffs.gql.tsx';

type TariffDetailsData = NonNullable<TariffQuery['tariff']>;

export type TariffHeaderData = Pick<
	TariffDetailsData,
	'tariffId' | 'name' | 'version'
>;

interface TariffDetailsProps {
	tariffId: string;
	activeTab?: string;
	onTabChange?: (tab: string) => void;
	hideTabsList?: boolean;
	showHeader?: boolean;
	onEdit?: (id: string) => void;
	onClone?: (id: string) => void;
	onDelete?: (id: string) => void;
	onOpenInNewWindow?: (id: string) => void;
	onClose?: () => void;
	onBack?: () => void;
	showBackButton?: boolean;
}

const TariffEntityIcon = () => (
	<div className="flex items-center justify-center w-14 h-14 rounded-full bg-transparent border-2 border-gray-400 dark:border-gray-500 flex-shrink-0">
		<BookOpen className="w-7 h-7 text-gray-600 dark:text-gray-300" />
	</div>
);

const getTariffStatusLabel = (status: number): string => {
	switch (status) {
		case 0:
			return 'Draft';
		case 1:
			return 'Active';
		case 2:
			return 'Inactive';
		default:
			return 'Unknown';
	}
};

export const TariffDetailsHeader = ({
	tariff,
	isMain,
	onSetMain,
	className,
	...actions
}: {
	tariff: TariffHeaderData;
	isMain?: boolean;
	onSetMain?: () => void;
	className?: string;
} & EntityDetailsHeaderActions) => {
	const { t } = useTranslation('tariffs');

	return (
		<EntityDetailsHeader
			icon={<TariffEntityIcon />}
			name={tariff.name}
			entityTypeName={tariff.version ? `v${tariff.version}` : undefined}
			entityId={actions.entityId ?? tariff.tariffId}
			onEdit={actions.onEdit}
			onClone={actions.onClone}
			onDelete={actions.onDelete}
			onOpenInNewWindow={actions.onOpenInNewWindow}
			onClose={actions.onClose}
			onBack={actions.onBack}
			showBackButton={actions.showBackButton}
			showCloseButton={actions.showCloseButton ?? Boolean(actions.onClose)}
			renderAction={onSetMain ? () => (
				<Button
					size="sm"
					variant={isMain ? 'ghost' : 'ghost'}
					onClick={onSetMain}
					disabled={isMain}
					className={`h-8 px-2.5 gap-1.5 rounded-lg transition-colors text-xs font-medium ${
						isMain
							? 'text-primary bg-primary/10 cursor-default'
							: 'text-muted-foreground hover:text-primary hover:bg-primary/10'
					}`}
					title={isMain ? t('setMain.alreadyMain') : t('setMain.buttonTitle')}
				>
					<Star className={`w-3.5 h-3.5 ${isMain ? 'fill-primary' : ''}`} />
					{isMain ? t('setMain.currentMain') : t('setMain.setAsMain')}
				</Button>
			) : undefined}
			className={className}
		/>
	);
};

export function TariffDetails({
	tariffId,
	activeTab,
	onTabChange,
	hideTabsList,
	showHeader,
	onEdit,
	onClone,
	onDelete,
	onOpenInNewWindow,
	onClose,
	onBack,
	showBackButton
}: TariffDetailsProps) {
	const { t } = useTranslation('common');
	const { t: tT } = useTranslation('tariffs');

	const { data, isLoading, isError } = useOne<TariffDetailsData>({
		resource: 'tariffs',
		id: tariffId,
		meta: {
			gqlQuery: TARIFF_QUERY,
			dataPath: 'tariff'
		}
	});

	const { shouldShowLoading } = useDelayedLoading(isLoading, {
		delay: 100,
		showInitialLoading: true
	});

	const tariff = data?.data;
	const { getRateTypeTitle } = useRateTypeLookup();
	const { openEntityModal } = useEntityModalContext();

	const formatRateSummary = (rate: { rateTypeEnum?: string; flatRateAmount?: number | string | null } | null | undefined): string | null => {
		if (!rate) return null;
		if (rate.rateTypeEnum === 'FLAT_RATE') {
			const amount = rate.flatRateAmount != null ? `€${Number(rate.flatRateAmount).toFixed(2)}` : '';
			return `${getRateTypeTitle('FLAT_RATE')}: ${amount}`;
		}
		return getRateTypeTitle(rate.rateTypeEnum ?? 'TIERED');
	};

	if (shouldShowLoading) {
		return (
			<div className="space-y-4">
				<div className="w-48 h-6 rounded bg-muted animate-pulse" />
				<div className="w-32 h-6 rounded bg-muted animate-pulse" />
				<div className="space-y-2">
					<div className="w-full h-4 rounded bg-muted animate-pulse" />
					<div className="w-3/4 h-4 rounded bg-muted animate-pulse" />
				</div>
			</div>
		);
	}

	if (!isLoading && (isError || !tariff)) {
		return (
			<div className="py-8 text-center text-muted-foreground">
				{tT('details.loadError')}
			</div>
		);
	}

	if (!tariff) return null;

	return (
		<div className="flex flex-col h-full">
			{showHeader && (
				<TariffDetailsHeader
					tariff={tariff}
					entityId={tariffId}
					onEdit={onEdit}
					onClone={onClone}
					onDelete={onDelete}
					onOpenInNewWindow={onOpenInNewWindow}
					onClose={onClose}
					onBack={onBack}
					showBackButton={showBackButton}
					showCloseButton={!!onClose}
					className="pb-4 mb-4"
				/>
			)}

			<DelayedSkeleton
				isLoading={isLoading}
				delay={300}
				showInitialLoading={false}
			>
				<Tabs
					defaultValue={
						activeTab !== undefined ? undefined : 'details'
					}
					value={activeTab}
					onValueChange={onTabChange}
					className="w-full flex-1"
				>
					{!hideTabsList && (
						<TabsList>
							<TabLink
								value="details"
								icon={FileText}
								label={t('tabs.details')}
							/>
							<TabLink
								value="audit"
								icon={History}
								label={t('tabs.audit')}
								align="right"
							/>
						</TabsList>
					)}

					<TabsContent value="details" className="space-y-6 mt-6">
						<DetailSection>
							<DetailFieldsGrid>
								<DetailField
									label={tT('details.version')}
									value={tariff.version || null}
								/>
								<DetailField
									label={tT('details.status')}
									value={getTariffStatusLabel(tariff.status)}
								/>
								<DetailField
									label={tT('details.description')}
									value={tariff.description || null}
									className="col-span-2"
								/>
							</DetailFieldsGrid>
						</DetailSection>

						<DetailSection title={tT('details.gracePeriods')}>
							<DetailFieldsGrid>
								<DetailField
									label={tT('details.entryGracePeriod')}
									value={`${tariff.entryGracePeriodMinutes} min`}
								/>
								<DetailField
									label={tT('details.postPaymentGrace')}
									value={`${tariff.postPaymentGraceMinutes} min`}
								/>
								<DetailField
									label={tT('details.freeExitMinutes')}
									value={`${tariff.freeExitMinutes} min`}
								/>
							</DetailFieldsGrid>
						</DetailSection>

						{(tariff.lostTicketFixedCharge != null || tariff.lostTicketRate || tariff.reEntryChargeRate) && (
							<DetailSection
								title={tT('details.lostTicketAndReentry')}
							>
								<DetailFieldsGrid>
									<DetailField
										label={tT('details.lostTicketFixedCharge')}
										value={
											tariff.lostTicketFixedCharge != null
												? `€${Number(tariff.lostTicketFixedCharge).toFixed(2)}`
												: null
										}
									/>
									<DetailField
										label={tT('details.lostTicketRate')}
										value={
											formatRateSummary(tariff.lostTicketRate)
										}
									/>
									<DetailField
										label={tT('details.reEntryChargeRate')}
										value={
											formatRateSummary(tariff.reEntryChargeRate)
										}
									/>
								</DetailFieldsGrid>
							</DetailSection>
						)}

						{tariff.sourceTariff && (
							<DetailSection title={tT('details.associations')}>
								<DetailFieldsGrid>
									<DetailField
										label={tT('details.sourceTariff')}
										value={tariff.sourceTariff?.name || null}
									/>
								</DetailFieldsGrid>
							</DetailSection>
						)}

						<DetailSection title={tT('details.pricingPoliciesTitle')}>
							{tariff.pricingPolicies.length === 0 ? (
								<p className="text-sm text-muted-foreground py-4">
									{tT('details.noPricingPolicies')}
								</p>
							) : (
								<div className="py-1 text-sm">
									{tariff.pricingPolicies.map(pp => (
										<div
											key={pp.pricingPolicyId}
											className="py-2 border-b-2 border-dotted border-border/60 last:border-b-0"
										>
											<EntityLink
												entityId={pp.pricingPolicyId}
												entityType="pricingPolicy"
												displayText={pp.name}
												onEntityClick={openEntityModal}
												className="text-sm"
											/>
										</div>
									))}
								</div>
							)}
						</DetailSection>

						<DetailSection title={tT('details.limitPoliciesTitle')}>
							{tariff.limitPolicies.length === 0 ? (
								<p className="text-sm text-muted-foreground py-4">
									{tT('details.noLimitPolicies')}
								</p>
							) : (
								<div className="py-1 text-sm">
									{tariff.limitPolicies.map(lp => (
										<div
											key={lp.limitPolicyId}
											className="py-2 border-b-2 border-dotted border-border/60 last:border-b-0"
										>
											<EntityLink
												entityId={lp.limitPolicyId}
												entityType="limitPolicy"
												displayText={lp.name}
												onEntityClick={openEntityModal}
												className="text-sm"
											/>
										</div>
									))}
								</div>
							)}
						</DetailSection>

						{tariff.needsVerification && (
							<DetailSection
								title={tT('details.verification')}
							>
								<DetailFieldsGrid>
									<DetailField
										label={tT(
											'details.verificationReason'
										)}
										value={
											tariff.verificationReason || null
										}
									/>
									<DetailField
										label={tT(
											'details.verificationFlaggedAt'
										)}
										value={
											tariff.verificationFlaggedAt
												? new Date(
														tariff.verificationFlaggedAt
													).toLocaleString()
												: null
										}
									/>
								</DetailFieldsGrid>
							</DetailSection>
						)}
					</TabsContent>

					<TabsContent value="audit" className="mt-6">
						<AuditTab
							data={{
								createdAt: tariff.createdAt,
								updatedAt: tariff.updatedAt,
								activatedAt: tariff.activatedAt,
								deactivatedAt: tariff.deactivatedAt
							}}
						/>
					</TabsContent>
				</Tabs>
			</DelayedSkeleton>
		</div>
	);
}
